import React, { createContext, useContext, useReducer, useCallback, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { initialState, chatReducer } from "../store/reducers/chatReducer";
import {
  createChatActions,
  createOrderChatHandler,
  createPartnerChatHandler,
  createOrderMessageChatHandler,
} from "../store/actions/chatActions";
import {
  useChatConnection,
  useChatSubscriptions,
  useChatAutoConnect,
  useChatCleanup,
  useRefreshChatCallback,
  useChat,
} from "../hooks/useChatHooks";
import ChatModal from "../components/shared/chat/ChatModal";
import { useLanguage } from "./LanguageContext";

export const ChatContext = createContext();

export const ChatProvider = ({ children, enabled = true }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { user } = useSelector((state) => state);
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const actions = useMemo(() => createChatActions(dispatch, t), [dispatch, t]);

  const handleConnectionStatus = useCallback(
    (data) => {
      actions.setConnectionStatus(data);
    },
    [actions],
  );

  const handleOrderChat = useCallback(
    (data) => {
      const handler = createOrderChatHandler(stateRef.current, actions, user, t, currentLanguage);
      return handler(data);
    },
    [actions, user, t, currentLanguage],
  );

  const handlePartnerChat = useCallback(
    (data) => {
      const handler = createPartnerChatHandler(stateRef.current, actions, user, t);
      return handler(data);
    },
    [actions, user, t],
  );

  const handleOrderMessageChat = useCallback(
    (data) => {
      const handler = createOrderMessageChatHandler(stateRef.current, actions, user, t, currentLanguage);
      return handler(data);
    },
    [actions, user, t, currentLanguage],
  );

  const { connectToChat, disconnectFromChat } = useChatConnection(enabled ? user?.companyId : null, actions);
  const { setRefreshCallback } = useRefreshChatCallback(state, actions);

  useChatSubscriptions(handleConnectionStatus, handleOrderChat, handlePartnerChat, handleOrderMessageChat);
  useChatAutoConnect(connectToChat);
  useChatCleanup();

  const value = useMemo(
    () => ({
      ...state,
      connectToChat,
      disconnectFromChat,
      openChatModal: actions.openChatModal,
      closeChatModal: actions.closeChatModal,
      setRefreshCallback,
      registerChat: actions.registerChat,
      unregisterChat: actions.unregisterChat,
    }),
    [state, connectToChat, disconnectFromChat, actions, setRefreshCallback],
  );

  return (
    <ChatContext.Provider value={value}>
      {children}
      {enabled && (
        <ChatModal
          isOpen={state.isChatModalOpen}
          orderId={state.chatOrderId}
          chatType={state.chatType}
          partnerId={state.chatPartnerId}
          isAirOrder={state.isAirOrder}
          onClose={actions.closeChatModal}
          setRefreshCallback={setRefreshCallback}
        />
      )}
    </ChatContext.Provider>
  );
};

export { useChat };
